import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

export default function Hero() {
  const [time, setTime] = useState("");

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(
        now.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: "UTC",
        }) + " UTC"
      );
    };
    update();
    const i = setInterval(update, 30000);
    return () => clearInterval(i);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-end pt-32 pb-16 overflow-hidden bg-[#F7F5F0]"
      data-testid="hero-section"
    >
      <div className="aurora" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#F7F5F0]" />

      {/* Vertical side rails */}
      <div className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-4 text-black/40">
        <div className="pulse-dot" />
        <span className="overline" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
          {time}
        </span>
      </div>
      <div className="hidden lg:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-3 text-black/40">
        <span className="overline" style={{ writingMode: "vertical-rl" }}>
          US / Worldwide
        </span>
      </div>

      <div className="container-x relative z-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="overline mb-8 flex items-center gap-3"
          data-testid="hero-overline"
        >
          <span className="pulse-dot" />
          Brilliant Speed · Radiant Logic · Est. 2025
        </motion.div>

        <h1 className="font-display font-black leading-[0.85] tracking-tighter mb-10 text-[#0A0A0A]" data-testid="hero-title">
          {["BUILD AT", "THE SPEED OF", "BARRAQ."].map((line, idx) => (
            <motion.span
              key={idx}
              initial={{ opacity: 0, y: 60, skewY: 4 }}
              animate={{ opacity: 1, y: 0, skewY: 0 }}
              transition={{ duration: 0.9, delay: 0.3 + idx * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="block text-[clamp(3rem,11vw,11rem)]"
            >
              {idx === 2 ? (
                <>
                  <span className="text-[#C99B1A] italic font-light">Barraq</span>
                  <span className="text-[#C99B1A]">.</span>
                </>
              ) : (
                line
              )}
            </motion.span>
          ))}
        </h1>

        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="lg:col-span-5 text-lg md:text-xl text-black/65 leading-relaxed max-w-xl"
            data-testid="hero-description"
          >
            We design AI systems that are not just fast — they are{" "}
            <span className="text-[#0A0A0A] font-medium">brilliant</span>. From CRMs that read your customers, to
            schools that run themselves, to research that opens new horizons.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="lg:col-span-4 lg:col-start-9 flex flex-col sm:flex-row gap-4"
          >
            <button onClick={() => scrollTo("services")} className="btn-primary" data-testid="hero-cta-primary">
              Explore Capabilities
              <ArrowUpRight size={16} />
            </button>
            <button onClick={() => scrollTo("contact")} className="btn-secondary" data-testid="hero-cta-secondary">
              Start a Project
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-20 flex items-center gap-3 text-black/40 text-xs uppercase tracking-[0.22em]"
        >
          <ArrowDown size={14} className="animate-bounce" />
          Scroll to discover
        </motion.div>
      </div>
    </section>
  );
}
